import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Table as TableIcon, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiPost } from '@/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const quoteSqlIdentifier = (name: string) => `"${String(name || '').replace(/"/g, '""')}"`;

interface DataModelPreviewTabsModalProps {
    isOpen: boolean;
    onClose: () => void;
    dataModel: any; // The Active Data Model containing tables
    initialTableName?: string;
}

export const DataModelPreviewTabsModal = ({ isOpen, onClose, dataModel, initialTableName }: DataModelPreviewTabsModalProps) => {
    const [activeTab, setActiveTab] = useState<string>('');
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Reset and initialize when modal opens or model changes
    useEffect(() => {
        if (isOpen && dataModel?.tables?.length > 0) {
            const firstTable = dataModel.tables[0].name || dataModel.tables[0];
            setActiveTab(initialTableName || firstTable);
        } else {
            setRows([]);
            setError(null);
            setActiveTab('');
        }
    }, [isOpen, dataModel, initialTableName]);

    // Fetch data when active tab changes
    useEffect(() => {
        if (isOpen && activeTab && dataModel?.id) {
            fetchPreview(activeTab);

            // Scroll active tab into view
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    const activeElement = scrollContainerRef.current.querySelector('[data-state="active"]');
                    if (activeElement) {
                        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                    }
                }
            }, 100);
        }
    }, [isOpen, activeTab, dataModel?.id]);

    const fetchPreview = async (tableName: string) => {
        setLoading(true);
        setError(null);
        setRows([]);
        try {
            if (!dataModel?.id) {
                throw new Error('No data model selected');
            }
            const tableSql = quoteSqlIdentifier(tableName);
            const responseData = await apiPost(`/datamodels/${dataModel.id}/query`, null, {
                sql: `SELECT * FROM ${tableSql} LIMIT 50`
            });
            setRows(Array.isArray(responseData?.rows) ? responseData.rows : []);
        } catch (e: any) {
            setError(e.message || "Failed to load preview");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !dataModel) return null;

    const tables = dataModel.tables || [];

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:p-8">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                    transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                    className="w-full max-w-[1400px] h-[85vh] bg-white dark:bg-slate-950 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800"
                >
                    {/* Header */}
                    <div className="h-16 shrink-0 px-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-md">
                        <div className="flex items-center gap-4 border-r border-slate-200 dark:border-slate-800 pr-6 mr-2">
                            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl ring-1 ring-inset ring-indigo-100 dark:ring-indigo-500/20 shadow-sm">
                                <Database size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 tracking-tight leading-none mb-1 shadow-sm">Data Preview</h2>
                                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                    <span className="text-slate-700 dark:text-slate-300">{dataModel.name || 'Data Model'}</span> • Top 50 Rows
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-hidden ml-2 flex items-center">
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
                                <div
                                    ref={scrollContainerRef}
                                    className="overscroll-x-none overscroll-contain overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide py-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                >
                                    <TabsList className="h-auto w-max inline-flex bg-transparent p-0 border-b-0 space-x-1.5 px-1">
                                        {tables.map((t: any) => {
                                            const tName = typeof t === 'string' ? t : t.name;
                                            return (
                                                <TabsTrigger
                                                    key={tName}
                                                    value={tName}
                                                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all border border-transparent"
                                                >
                                                    <TableIcon size={14} className="mr-2 inline" />
                                                    {tName}
                                                </TabsTrigger>
                                            );
                                        })}
                                    </TabsList>
                                </div>
                            </Tabs>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 rounded-full transition-colors ml-4">
                            <X size={20} />
                        </Button>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-auto bg-slate-50/50 dark:bg-slate-950 p-6">
                        {loading ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-4">
                                <div className="w-16 h-16 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                                </div>
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 animate-pulse bg-white/50 dark:bg-slate-900/50 px-4 py-1.5 rounded-full border border-slate-100/50 dark:border-slate-800/50">Fetching 50 rows from <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{activeTab}</span>...</p>
                            </div>
                        ) : error ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center max-w-md p-8 bg-white dark:bg-slate-900 rounded-2xl border border-red-100 dark:border-red-900/30 shadow-sm">
                                    <div className="w-14 h-14 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-4 ring-1 ring-inset ring-red-100 dark:ring-red-900/50">
                                        <X size={28} />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Preview Failed</h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-950 p-3 rounded-lg border border-slate-100 dark:border-slate-800 font-mono text-left overflow-x-auto whitespace-pre-wrap break-words inline-block">{error}</p>
                                    <Button variant="outline" size="sm" onClick={() => fetchPreview(activeTab)} className="mt-6 text-slate-600">Try Again</Button>
                                </div>
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center max-w-sm p-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800 shadow-sm">
                                    <div className="w-16 h-16 bg-slate-50 dark:bg-slate-950 text-slate-400 dark:text-slate-500 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-inset ring-slate-200 dark:ring-slate-800">
                                        <TableIcon size={28} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">Empty Table</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">There is no data available in <span className="font-semibold text-slate-700 dark:text-slate-300">{activeTab}</span>.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm inline-block min-w-full align-middle ring-1 ring-slate-900/5 dark:ring-white/5">
                                <div className="overflow-x-auto relative">
                                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-sm border-collapse">
                                        <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 top-0">
                                            <tr>
                                                <th className="px-4 py-3.5 text-center font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider text-[10px] w-12 border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 sticky left-0 z-20">#</th>
                                                {Object.keys(rows[0] || {}).map((col, idx) => (
                                                    <th key={col} className={`px-5 py-3.5 text-left font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest text-[11px] whitespace-nowrap bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-sm sticky top-0 z-10 ${idx !== Object.keys(rows[0] || {}).length - 1 ? 'border-r border-slate-100 dark:border-slate-800/60' : ''}`}>
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/40"></div>
                                                            {col}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 bg-white dark:bg-slate-900">
                                            {rows.map((row, i) => (
                                                <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-500/5 transition-colors group">
                                                    <td className="px-4 py-2.5 text-slate-400 dark:text-slate-500 text-[11px] text-center border-r border-slate-100 dark:border-slate-800 font-mono group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:bg-indigo-50/60 dark:group-hover:bg-indigo-500/10 transition-colors bg-slate-50/30 dark:bg-slate-900/30">
                                                        {i + 1}
                                                    </td>
                                                    {Object.keys(row || {}).map((col, idx) => (
                                                        <td key={col} className={`px-5 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[320px] ${idx !== Object.keys(row || {}).length - 1 ? 'border-r border-slate-50 dark:border-slate-800/30' : ''}`} title={typeof row[col] === 'object' && row[col] !== null ? JSON.stringify(row[col]) : String(row[col] ?? '')}>
                                                            {(() => {
                                                                const val = row[col];
                                                                if (val === null || val === undefined) return <span className="text-slate-300 dark:text-slate-600 italic text-xs">null</span>;
                                                                if (typeof val === 'object') return <span className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800/80 px-1.5 py-0.5 rounded text-indigo-600 dark:text-indigo-400 border border-slate-200 dark:border-slate-700/50 shadow-sm">{JSON.stringify(val)}</span>;

                                                                // Format booleans visually
                                                                if (typeof val === 'boolean') {
                                                                    return val ?
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase bg-emerald-100/80 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/20">True</span> :
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 ring-1 ring-inset ring-slate-500/20">False</span>;
                                                                }

                                                                return <span className="text-[13px]">{String(val)}</span>;
                                                            })()}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
